/* eslint-disable @typescript-eslint/no-explicit-any */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { InputProps as MuiInputProps, TextField } from "@material-ui/core";
import { ChangeEvent, FC, Ref, useEffect, useState } from "react";
import {
  OnChangeTranslations,
  Translations,
} from "src/components/molecules/modals/TranslationsModal/types";
import useDelayedEffect from "src/hooks/utils/useDelayedEffect";
import { formatMsg } from "src/utils/intl/intlFormmaters";
import { noop } from "../../../../globalConsts";
import AutoCloseTooltip from "../../AutoCloseTooltip";
import useAutoCloseTooltipDebouncer from "../../AutoCloseTooltip/useAutoCloseDebounce";
import { textFieldCss } from "../commonStyles";
import TranslationBtnWithModal from "../EwaButtons/TranslationBtnWithModal";
import { AUTOCOMPLETE_DISABLE_VALUE } from "../FlowioInput/constants";
import InputComponent from "../InputComponent";
import { InputContainer } from "./styles";
import { sentenceContainsLongWord, shortenLongWord } from "./utils";

type ControlledOnlyProps = {
  value: string | number;
  defaultValue?: undefined;
  inputRef?: Ref<any>;
};
type UncontrolledOnlyProps = {
  value?: undefined;
  defaultValue: string | number;
  inputRef: Ref<any>;
};
type RefControlledOnlyProps = {
  value?: undefined;
  defaultValue?: undefined;
  inputRef: Ref<any>;
};

type Config = { maxLength?: number };

type CommonProps = {
  onChange?: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  className?: string;
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  InputProps?: MuiInputProps;
  config?: Config;
  autoFocus?: boolean;
  disablePrefill?: boolean;
  limitLongWords?: boolean;
  translations?: Translations;
  onChangeTranslation?: OnChangeTranslations;
};

type Props = (
  | UncontrolledOnlyProps
  | ControlledOnlyProps
  | RefControlledOnlyProps
) &
  CommonProps;

const FlowioTextInput: FC<Props> = ({
  value,
  defaultValue,
  onChange = noop,
  inputRef,
  placeholder,
  disabled = false,
  name,
  id,
  readOnly,
  onClick = noop,
  onBlur = noop,
  onFocus = noop,
  required = false,
  className,
  InputProps,
  config: { maxLength } = {},
  autoFocus,
  limitLongWords = false,
  disablePrefill = false,
  translations,
  onChangeTranslation,
}) => {
  const autoComplete = disablePrefill ? AUTOCOMPLETE_DISABLE_VALUE : undefined;
  const [hasError, setHasError] = useState(false);
  const [tooltipMsg, setTooltipMsg] = useState("");
  const { debouncedSetTooltipVisible, showTooltip } =
    useAutoCloseTooltipDebouncer();

  // used for no perform on first render, in this case i want to assign hasError prop after some user action
  useDelayedEffect(() => {
    const stateToChange = required && !value && value !== 0;

    if (hasError !== stateToChange) setHasError(stateToChange);
  }, [required, value]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newVal = String(e.target.value);

    if (
      limitLongWords &&
      typeof sentenceContainsLongWord(newVal) === "string"
    ) {
      debouncedSetTooltipVisible(true);
      setTooltipMsg(formatMsg("input.text.maxLength"));
      onChange(shortenLongWord(newVal));
      return;
    }

    onChange(newVal);
  };

  useEffect(() => {
    if (!value && required) {
      setHasError(true);
    }
  }, [value, required]);

  const hasTranslations = !!translations && !!onChangeTranslation;

  return (
    <InputContainer
      hasTranslations={hasTranslations}
      key={`${name}-${id}-${maxLength}`}
    >
      <AutoCloseTooltip
        onTimerElapsed={debouncedSetTooltipVisible}
        visible={showTooltip}
        message={tooltipMsg}
      >
        <TextField
          css={textFieldCss(hasError)}
          type="text"
          value={value}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          InputProps={{
            defaultValue,
            disableUnderline: true,
            inputComponent: InputComponent,
            inputRef,
            name,
            id,
            readOnly,
            onBlur,
            onFocus,
            ...InputProps,
          }}
          autoFocus={autoFocus}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{
            maxLength,
            autoComplete,
          }}
          className={className}
          onClick={onClick}
          data-readonly={readOnly}
        />
      </AutoCloseTooltip>
      {hasTranslations && (
        <TranslationBtnWithModal
          translations={translations}
          onChangeTranslations={onChangeTranslation}
        />
      )}
    </InputContainer>
  );
};

export default FlowioTextInput;
export type FlowioTextInputProps = Props;
